import { useEffect, useMemo, useRef } from "react";

import type { SuggestedChipItemProps } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterSuggestionsBar";
import { useStagedSuggestions } from "app/pages/sales-intelligence/common-components/filters-panel/hooks/useStagedSuggestions";
import type {
    IndustryNodeId,
    IndustryTaxonomyIndex,
} from "app/pages/sales-intelligence/domain/industry/types/taxonomy";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";

import useIndustryAffinityQuery from "../queries/useIndustryAffinityQuery";
import industryTrackingService from "../services/tracking/trackingService";
import { useIndustrySuggestionsStore } from "../store/useIndustrySuggestionsStore";
import { createIndustrySuggestionsViewModel } from "../view/industry-suggestions/factory";

import { useSuggestionsImpression } from "./useSuggestionsImpression";

/** Matches the legacy industry suggestions, so both cards reveal new chips at the same pace. */
const SUGGESTIONS_STAGE_DELAY_MS = 1500;

type UseIndustryTaxonomySuggestionsParams = {
    readonly values: readonly IndustryNodeId[];
    readonly inclusion: InclusionOption;
    readonly taxonomy: IndustryTaxonomyIndex;
    readonly isExpanded: boolean;
    /** Adds the node to the card's selection in its current mode. */
    readonly onApply: (id: IndustryNodeId) => void;
};

type IndustryTaxonomySuggestions = {
    readonly suggestedItems: readonly SuggestedChipItemProps[];
    readonly isEnabled: boolean;
    readonly isLoading: boolean;
};

export const useIndustryTaxonomySuggestions = ({
    values,
    inclusion,
    taxonomy,
    isExpanded,
    onApply,
}: UseIndustryTaxonomySuggestionsParams): IndustryTaxonomySuggestions => {
    const hasSelection = values.length > 0;
    const affinityQuery = useIndustryAffinityQuery({ enabled: hasSelection });

    const dismissedIds = useIndustrySuggestionsStore((state) => state.dismissedIds);
    const dismiss = useIndustrySuggestionsStore((state) => state.dismiss);
    const resetDismissals = useIndustrySuggestionsStore((state) => state.resetDismissals);

    const viewModel = useMemo(
        () => createIndustrySuggestionsViewModel({ values, taxonomy, affinity: affinityQuery.data, dismissedIds }),
        [values, taxonomy, affinityQuery.data, dismissedIds],
    );

    // JSON rather than a join: node ids are opaque and may contain any separator.
    const selectionKey = JSON.stringify(values);
    const { visibleIds, isStaging } = useStagedSuggestions(viewModel.topIds, {
        stageKey: selectionKey,
        delayMs: SUGGESTIONS_STAGE_DELAY_MS,
    });
    const visibleSuggestions = useMemo(() => viewModel.resolve(visibleIds), [viewModel, visibleIds]);

    // Only a clear resets: a card that mounts empty (another page, a new rule) must not wipe the session's dismissals.
    const hadSelection = useRef(hasSelection);

    useEffect(() => {
        if (hadSelection.current && !hasSelection) {
            resetDismissals();
        }

        hadSelection.current = hasSelection;
    }, [hasSelection, resetDismissals]);

    const isEnabled = hasSelection && affinityQuery.isSuccess;

    useSuggestionsImpression({
        signature: `${inclusion}|${selectionKey}`,
        isShowing: isExpanded && isEnabled && !isStaging && visibleSuggestions.length > 0,
        isExpanded,
        onImpression: () =>
            industryTrackingService.trackSuggestionsImpression(inclusion, {
                appliedNodeIds: values,
                suggestions: visibleSuggestions,
            }),
    });

    const suggestedItems = useMemo(
        () =>
            visibleSuggestions.map(
                ({ id, label, score }, position): SuggestedChipItemProps => ({
                    id,
                    text: label,
                    onApply: () => {
                        onApply(id);
                        industryTrackingService.trackSuggestionAdd(inclusion, {
                            nodeId: id,
                            score,
                            position,
                        });
                    },
                    onDismiss: () => {
                        const replacementNodeId = viewModel.replacementFor(id, visibleIds);

                        dismiss(id);
                        industryTrackingService.trackSuggestionDismiss(inclusion, {
                            nodeId: id,
                            replacementNodeId,
                        });
                    },
                }),
            ),
        [visibleSuggestions, viewModel, visibleIds, onApply, inclusion, dismiss],
    );

    return { suggestedItems, isEnabled, isLoading: isStaging };
};
